/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import { useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { CityStats, GameStatus, Grid, Milestone, RandomEvent, ResearchState } from '../types';
import type { ActiveModal } from '../components/UIOverlay';
import { useNewsFeed, useAiNarrative } from './useAiNarrative';
import { useScenarioFlow } from './useScenarioFlow';

export interface UseGameNarrativeSetupDeps {
  aiEnabled: boolean;
  stats: CityStats;
  grid: Grid;
  setGrid: Dispatch<SetStateAction<Grid>>;
  setGameStatus: (status: GameStatus) => void;
  setGameStarted: (value: boolean) => void;
  setStats: Dispatch<SetStateAction<CityStats>>;
  setResearch: Dispatch<SetStateAction<ResearchState>>;
  setMilestones: (milestones: Milestone[] | ((prev: Milestone[]) => Milestone[])) => void;
  setActiveModal: (modal: ActiveModal) => void;
  gameStatus: GameStatus;
}

export function useGameNarrativeSetup({
  aiEnabled,
  stats,
  grid,
  setGrid,
  setGameStatus,
  setGameStarted,
  setStats,
  setResearch,
  setMilestones,
  setActiveModal,
  gameStatus,
}: UseGameNarrativeSetupDeps) {
  const { newsFeed, setNewsFeed, addNewsItem } = useNewsFeed();
  const [activeEvent, setActiveEvent] = useState<RandomEvent | null>(null);

  const {
    activeScenario,
    setActiveScenario,
    scenarioObjectives,
    setScenarioObjectives,
    activeQuiz,
    setActiveQuiz,
    loadScenario,
  } = useScenarioFlow({
    addNewsItem,
    setNewsFeed,
    setMilestones,
    setGameStatus,
    setGameStarted,
    setStats,
    setResearch,
    setGrid,
    setActiveModal,
    gameStatus,
  });

  const {
    currentGoal,
    setCurrentGoal,
    isGeneratingGoal,
    currentStory,
    triggerStory,
    fetchNewGoal,
    fetchNews,
    handleAskAICoach,
    handleAutoSolve,
    handleClaimReward,
  } = useAiNarrative({
    aiEnabled,
    activeScenario,
    stats,
    grid,
    setGrid,
    setStats,
    addNewsItem,
  });

  return {
    newsFeed,
    setNewsFeed,
    addNewsItem,
    activeEvent,
    setActiveEvent,
    activeScenario,
    setActiveScenario,
    scenarioObjectives,
    setScenarioObjectives,
    activeQuiz,
    setActiveQuiz,
    loadScenario,
    currentGoal,
    setCurrentGoal,
    isGeneratingGoal,
    currentStory,
    triggerStory,
    fetchNewGoal,
    fetchNews,
    handleAskAICoach,
    handleAutoSolve,
    handleClaimReward,
  };
}